import { Clock, Coins, Radio } from "lucide-react";
import { MethodBadge } from "./Badge";
import { ParamTable, type ParamRow } from "./ParamTable";
import { ResponseExample } from "./ResponseExample";
import { CodeBlock } from "./CodeBlock";

interface EndpointCardProps {
  id: string;
  method: "GET" | "POST" | "PATCH" | "DELETE";
  path: string;
  summary: string;
  description?: string;
  timing?: string;
  cost?: string;
  sse?: boolean;
  requestBody?: ParamRow[];
  queryParams?: ParamRow[];
  pathParams?: ParamRow[];
  responseExample?: string;
  curl: string;
}

export function EndpointCard({
  id,
  method,
  path,
  summary,
  description,
  timing,
  cost,
  sse,
  requestBody,
  queryParams,
  pathParams,
  responseExample,
  curl,
}: EndpointCardProps) {
  return (
    <section
      id={id}
      className="glass-card rounded-xl p-6 space-y-5 scroll-mt-6"
    >
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <MethodBadge method={method} />
        <code className="text-sm font-mono font-semibold text-base-content">
          {path}
        </code>
      </div>

      {/* Summary */}
      <p className="text-sm font-medium text-base-content">
        {summary}
      </p>

      {/* Description */}
      {description && (
        <p className="text-sm text-base-content/70 leading-relaxed">
          {description}
        </p>
      )}

      {/* Meta badges */}
      {(timing || cost || sse) && (
        <div className="flex items-center gap-2 flex-wrap">
          {timing && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-base-200 text-[11px] text-base-content/60 border border-base-content/10">
              <Clock className="w-3 h-3" />
              {timing}
            </span>
          )}
          {cost && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-base-200 text-[11px] text-base-content/60 border border-base-content/10">
              <Coins className="w-3 h-3" />
              {cost}
            </span>
          )}
          {sse && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-500/15 text-[11px] text-violet-400 border border-violet-500/25">
              <Radio className="w-3 h-3" />
              SSE
            </span>
          )}
        </div>
      )}

      {/* Path Parameters */}
      {pathParams && pathParams.length > 0 && (
        <ParamTable title="Path Parameters" params={pathParams} />
      )}

      {/* Query Parameters */}
      {queryParams && queryParams.length > 0 && (
        <ParamTable title="Query Parameters" params={queryParams} />
      )}

      {/* Request Body */}
      {requestBody && requestBody.length > 0 && (
        <ParamTable title="Request Body" params={requestBody} />
      )}

      {/* Response Example */}
      {responseExample && (
        <ResponseExample
          status={method === 'POST' && !path.includes('draft') && !path.includes('generate') && !path.includes('research') ? 201 : 200}
          description="Success"
          body={responseExample}
        />
      )}

      {/* cURL example */}
      <div className="space-y-2">
        <h4 className="text-sm font-semibold text-base-content">Example</h4>
        <CodeBlock code={curl} language="bash" />
      </div>
    </section>
  );
}
